import React from "react"
import {
  ValueWithLabelContainer,
  ValueWithLabelTemplate,
} from "../../../components/layouts/containers/ValueWithLabelElements.container"
import { CLIENT_BUDGET_TYPES } from "../../../consts/modules/clients.consts"
import { numberFormatter } from "../../../utils/numbers.utils"
import { displayValueOrPlaceholder } from "../../../utils/strings.utils"
import SprintEstimatedBudgetEnd from "./SprintEstimatedBudgetEnd.molecule"
import { getDateTime } from "../../../utils/dateTime.utils"
import SprintBudgetConsumptionProgress from "./SprintBudgetConsumptionProgress.molecule"
import { getEstimatedBudgetEnd } from "../../../utils/budget.utils"
import { formatDateRange } from "../../../utils/date.utils"

export const budgetTypeCreditTemplate = ({
  suggestedMonthlyBudgetLimit,
  lastMonthBilledTime,
  estimatedEndDate,
  loading = false,
  refChart,
}) => {
  return (
    <ValueWithLabelContainer className="flex-1" grid>
      <ValueWithLabelTemplate
        label="Consumo sugerido"
        value={displayValueOrPlaceholder(suggestedMonthlyBudgetLimit)}
        loading={loading}
        cols={6}
      />
      <ValueWithLabelTemplate
        label="Horas consumidas"
        value={numberFormatter(lastMonthBilledTime) ?? "-"}
        loading={loading}
        cols={6}
        showZeroValue
      />
      <ValueWithLabelTemplate
        label="Fecha de renovación"
        value={
          <SprintEstimatedBudgetEnd
            estimatedEndDate={getDateTime()
              .plus({ months: 1 })
              .startOf("month")
              .toFormat("dd/MM/yyyy")}
          />
        }
        loading={loading}
        cols={12}
        className="min-w-min"
      />
      <ValueWithLabelTemplate
        label="Disponibilidad de presupuesto"
        value={
          <div ref={refChart}>
            <SprintBudgetConsumptionProgress
              data={{
                suggestedMonthlyBudgetLimit,
                lastMonthBilledTime,
                estimatedEndDate,
                clientBudgetTypeId: CLIENT_BUDGET_TYPES.CREDIT,
              }}
            />
          </div>
        }
        loading={loading}
        cols={12}
      />
    </ValueWithLabelContainer>
  )
}

export const budgetTypeDebitTemplate = ({
  totalBudget,
  historicalBudget,
  totalBilledTime,
  hasBudget,
  lastEightWeeksTruncatedMean,
  estimatedEndDate,
  isPresentationMode = false,
  loading = false,
  refChart,
  statusVisible = true,
  extraData = {},
}) => {
  const budgetEndDate =
    isPresentationMode ?
      estimatedEndDate && formatDateRange(estimatedEndDate, estimatedEndDate)
    : (totalBudget || historicalBudget) &&
      getEstimatedBudgetEnd(
        totalBudget - totalBilledTime,
        lastEightWeeksTruncatedMean * 4,
        lastEightWeeksTruncatedMean * 4
      )?.estimatedEndMaxDate &&
      formatDateRange(
        getEstimatedBudgetEnd(
          totalBudget - totalBilledTime,
          lastEightWeeksTruncatedMean * 4,
          lastEightWeeksTruncatedMean * 4
        )?.estimatedEndMaxDate,
        getEstimatedBudgetEnd(
          totalBudget - totalBilledTime,
          lastEightWeeksTruncatedMean * 4,
          lastEightWeeksTruncatedMean * 4
        )?.estimatedEndMaxDate
      )
  const shouldShowBudgetData =
    hasBudget || extraData?.accumulatedPeriodConsumption != null

  return (
    <>
      <div className="flex flex-wrap gap-3 w-full">
        <div className="grid-column grid-column--rest">
          <ValueWithLabelTemplate
            label="Consumo total al corte"
            labelTooltip
            labelTooltipContent="Consumo total al corte"
            labelTooltipIcon="pi pi-info-circle"
            labelTooltipPosition={"right"}
            value={
              shouldShowBudgetData ?
                <span className="value-with-unit status-sprint">
                  {numberFormatter(totalBilledTime)}
                  <span className="unit"> horas</span>
                </span>
              : "-"
            }
            loading={loading}
          />
        </div>
        <div className="grid-column grid-column--rest">
          <ValueWithLabelTemplate
            label="Horas disponibles"
            value={
              shouldShowBudgetData ?
                <span className="value-with-unit status-sprint">
                  {numberFormatter(
                    totalBudget - totalBilledTime > 0 ?
                      totalBudget - totalBilledTime
                    : 0
                  )}

                  <span className="unit">
                    /{numberFormatter(totalBudget)} horas
                  </span>
                </span>
              : "-"
            }
            loading={loading}
            showZeroValue
          />
        </div>
        <div className="grid-column grid-column--rest">
          <ValueWithLabelTemplate
            label={`Fin estimado del presupuesto ${
              (
                getEstimatedBudgetEnd(
                  totalBudget - totalBilledTime,
                  lastEightWeeksTruncatedMean * 4,
                  lastEightWeeksTruncatedMean * 4
                )?.estimatedEndMaxDate > getDateTime()
              ) ?
                ""
              : ""
            }`}
            value={
              <SprintEstimatedBudgetEnd
                estimatedEndDate={
                  extraData?.estimatedEndDate ?
                    `~ ${formatDateRange(
                      extraData.estimatedEndDate,
                      extraData.estimatedEndDate
                    )}`
                  : budgetEndDate ?
                    `~ ${budgetEndDate}`
                  : null
                }
                tooltip
                tooltipValue="Calculado según el consumo promedio semanal, según las últimas 8 semanas"
              />
            }
            loading={loading}
            className="min-w-min"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 w-full mt-1">
        <div className="w-full">
          <ValueWithLabelTemplate
            label="Porcentaje de consumo"
            value={
              <div ref={refChart}>
                <SprintBudgetConsumptionProgress
                  data={{
                    totalBudget,
                    totalBilledTime,
                    clientBudgetTypeId: CLIENT_BUDGET_TYPES.DEBIT,
                  }}
                />
              </div>
            }
            loading={loading}
          />
        </div>
      </div>
    </>
  )
}
